var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * This function was copied from the creators of ModuleFederation which is why a few things in it are a bit
 * weird.
 *
 * It loads a Module wrapped up by Module federation. It returns a callback function. When you call it, it returns
 * a Promise that resolves to the Module it is loading.
 * @param remote - Syncs up with ModuleFederation 'name'.
 * @param sharedScope - Just pass in the string 'default'
 * @param moduleName - Syncs up with ModuleFederation 'exposes'
 * @param url - Normally remoteEntry.js
 * @returns A callback function to initialize the loading.
 * The Module that is resolved should have module.default.psMfeInit and then you can call that to
 * bootstrap the MFE.
 */
export const loadComponent = (remote, sharedScope, moduleName, url, throwErrors) => {
    //console.log(`loadComponent --- Remote: ${remote} Module: ${module} URL:  ${url}`);
    return () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        let module;
        let factory;
        try {
            yield getOrLoadRemote(remote, sharedScope, url);
        }
        catch (error) {
            switch (error.message) {
                case 'URL_ERROR':
                    console.error('*********************'
                        + '\n' + 'The URL prop passed into the dynamic component is not valid. This corresponds to the exposes value in module federation.'
                        + '\n' + 'URL: ' + url
                        + '\n' + 'REMOTE: ' + remote
                        + '\n' + 'MODULE: ' + moduleName
                        + '\n' + '*********************');
                    break;
                case 'REMOTE_ERROR':
                    {
                        console.error('*********************'
                            + '\n' + 'The REMOTE prop passed into the dynamic component is not valid. This corresponds to the exposes value in module federation.'
                            + '\n' + 'URL: ' + url
                            + '\n' + 'REMOTE: ' + remote
                            + '\n' + 'MODULE: ' + moduleName
                            + '\n' + '*********************');
                        // Remove the script of failed remote, in case of repeated reload attempts
                        // This allows a fully fresh reload when there was an error and fixes an edge case bug with reloading
                        const existingRemote = document.querySelector(`[data-webpack="${remote}"]`);
                        if (existingRemote != null) {
                            (_a = existingRemote.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild((existingRemote));
                        }
                    }
                    break;
                default:
                    console.error(error);
                    break;
            }
            if (throwErrors === true) {
                throw Error(error.message);
            }
            return;
        }
        try {
            // getOrLoadRemote puts sis, or Schoology, or hrms on the window.
            // Once the promise is resolved, we can get it off the window and start executing functions on it
            const container = window[remote];
            // The get function returns a factory function, which can generate the Module object.
            factory = yield container.get(moduleName);
            if (factory != null) {
                module = factory();
            }
            else {
                throw Error('MODULE_ERROR');
            }
            return module;
        }
        catch (error) {
            console.error('*********************'
                + '\n' + 'The MODULE prop passed into the dynamic component is not valid. This corresponds to the exposes value in module federation.'
                + '\n' + 'URL: ' + url
                + '\n' + 'REMOTE: ' + remote
                + '\n' + 'MODULE: ' + moduleName
                + '\n' + '*********************');
            if (throwErrors === true) {
                throw Error('MODULE_ERROR');
            }
        }
    });
};
/**
* The Right Way to Load Dynamic Remotes
* @author ScriptedAlchemy <zackary.l.jackson@gmail.com>
* @see {@link https://gist.github.com/ScriptedAlchemy/3a24008ef60adc47fad1af7d3299a063}
*
* @param {string} remote - the remote global name
* @param {object | string} shareScope - the shareScope Object OR scope key
* @param {string} remoteFallbackUrl - fallback url for remote module
* @returns {Promise<object>} - Federated Module Container
*/
export const getOrLoadRemote = (remote, shareScope, remoteFallbackUrl) => {
    return new Promise((resolve, reject) => {
        //console.log(`getOrLoadRemote --- Remote: ${remote}`);
        // check if remote exists on window, we don't need to instantiate it twice
        if (!window[remote]) {
            // search dom to see if remote tag exists, but might still be loading (async)
            const existingRemote = document.querySelector(`[data-webpack="${remote}"]`);
            // when remote is loaded..
            const onload = () => __awaiter(void 0, void 0, void 0, function* () {
                // check if it was initialized
                const theRemote = window[remote];
                if (theRemote == null) {
                    reject(new Error('REMOTE_ERROR'));
                }
                else if (!theRemote.__initialized) {
                    // if share scope doesnt exist (like in webpack 4) then expect shareScope to be a manual object
                    if (typeof __webpack_share_scopes__ === 'undefined') {
                        // use default share scope object passed in manually
                        yield theRemote.init(shareScope.default);
                    }
                    else {
                        // otherwise, init share scope as usual
                        yield theRemote.init(__webpack_share_scopes__[shareScope]);
                    }
                    // mark remote as initialized
                    theRemote.__initialized = true;
                }
                // resolve promise so marking remote as loaded
                resolve();
            });
            if (existingRemote && (existingRemote.src === remoteFallbackUrl) && (existingRemote.dataset['webpack'] === remote)) {
                // if existing remote but not loaded, hook into its onload and wait for it to be ready
                const existingOnload = existingRemote.onload;
                existingRemote.onload = function (...args) {
                    existingOnload.apply(this, args);
                    onload.apply(this, args);
                };
                existingRemote.onerror = function (...args) {
                    reject.apply(this, args);
                    reject(new Error('REMOTE_ERROR'));
                };
                // check if remote fallback exists as param passed to function
                // TODO: should scan public config for a matching key if no override exists
            }
            else if (remoteFallbackUrl) {
                // inject remote if a fallback exists and call the same onload function
                const d = document, script = d.createElement('script');
                script.type = 'text/javascript';
                // mark as data-webpack so runtime can track it internally
                script.setAttribute('data-webpack', `${remote}`);
                script.async = true;
                script.onerror = function (...args) {
                    reject(new Error('URL_ERROR'));
                };
                script.onload = onload;
                script.src = remoteFallbackUrl;
                try {
                    //console.log('appending to head');
                    d.getElementsByTagName('head')[0].appendChild(script);
                }
                catch (e) {
                    console.error(e);
                    reject(`Cannot Find Remote ${remote} to inject`);
                }
            }
            else {
                // no remote and no fallback exist, reject
                reject(`Cannot Find Remote ${remote} to inject`);
            }
        }
        else {
            // remote already instantiated, resolve
            resolve();
        }
    });
};
